import { Feature, GeometryObject } from "geojson";
import { LeafletPanelBody, LeafletPanelHeading } from "../shared/LeafletPanel";
import PanelInfoTable, { PanelInfoTableProps } from "../shared/PanelInfoTable";

import geojson from "geojson";
import { observer } from "mobx-react-lite";
import { useLocaleContext } from "../contexts/LocaleContext";
import { useRootStore } from "../models/Root";
import { Div, Section } from "trunx";
import { SyntheticEvent } from "react";

const layerDescriptionStyles = `
  :root {
    color-scheme: dark;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
      "Helvetica Neue", Arial, sans-serif;
    color: #fff;
    background: #343c3d;
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0 !important;
    color: #fff !important;
    background: #343c3d !important;
  }
  body { padding: 0.75rem; }
  table {
    width: 100% !important;
    border-collapse: collapse !important;
    color: inherit !important;
    background: transparent !important;
  }
  tr {
    color: #fff !important;
    background: #343c3d !important;
  }
  tr:nth-child(even) {
    background: #2f3637 !important;
  }
  td {
    padding: 0.5em 0.75em !important;
    border-bottom: 1px solid #8c9b9d !important;
    color: inherit !important;
    background: transparent !important;
  }
  tr:first-child > td {
    font-weight: 600;
    background: #282f2f !important;
  }
  a { color: #1abc9c !important; }
`;

const styleLayerDescription = (
  event: SyntheticEvent<HTMLIFrameElement>
) => {
  const document = event.currentTarget.contentDocument;
  if (!document || document.getElementById("jefmap-layer-description-styles")) {
    return;
  }

  const style = document.createElement("style");
  style.id = "jefmap-layer-description-styles";
  style.textContent = layerDescriptionStyles;
  document.head.appendChild(style);
};

const hasDescription = (
  feature: any
): feature is geojson.Feature<geojson.MultiPoint, { description: string }> => {
  return feature?.properties?.description !== undefined;
};

export interface LayerInfoPanelProps {
  feature: Feature<GeometryObject>;
}

const LayerInfoPanel = ({ feature }: LayerInfoPanelProps) => {
  const {
    lang,
    dict: {
      panel: { layers: dict },
    },
  } = useLocaleContext();

  const {
    layers: { clearSelectedFeature },
  } = useRootStore();

  if (hasDescription(feature)) {
    const { properties: description } = feature;
    return (
      <Div bulma="panel" className="layer-description-panel">
        <Div bulma="panel-heading">
          <LeafletPanelHeading
            title={dict.title}
            close={clearSelectedFeature}
          />
        </Div>
        <iframe
          className="layer-description-frame"
          title="description"
          srcDoc={description.description}
          onLoad={styleLayerDescription}
        ></iframe>
      </Div>
    );
  } else {
    // No explicit description, just show all properties in a table
    const properties = (feature as any)?.properties || {
      incidentname: "Unknown",
    };
    const incidentName =
      properties.irwin_IncidentName ||
      properties.IncidentName ||
      properties.incidentname ||
      properties.poly_IncidentName ||
      properties.IncidentName ||
      properties.name;

    const humanize = (row: [string, any]): [string, string] => {
      let [k, v] = row;
      k = k.replace(/poly_/g, " ");
      k = k.replace(/attr_/g, " ");
      k = k.replace(/_dt/g, " ");
      k = k.replace(/([A-Z]+)*([A-Z][a-z])/g, "$1 $2")

      const maybeDate = new Date(v);
      const isDate = !isNaN(maybeDate as any) && maybeDate.getFullYear() > 2000;
      const v_ = isDate ? maybeDate.toLocaleString(lang) : v && v.toString();
      return [k, v_];
    };

    const rows: PanelInfoTableProps["rows"] =
      Object.entries(properties).map(humanize);

    return (
      <Div bulma="panel">
        <Div bulma="panel-heading">
          <LeafletPanelHeading
            title={incidentName}
            close={clearSelectedFeature}
          />
        </Div>

        <LeafletPanelBody>
          <Section>
            <PanelInfoTable rows={rows} />
          </Section>
        </LeafletPanelBody>
      </Div>
    );
  }
};

export default observer(LayerInfoPanel);
